import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import type { Applications } from '@freelancer/config';
import { AppsDomainsMap, APPS_DOMAINS_MAP } from '@freelancer/config';
import type { QueryParams } from '@freelancer/ui/link';
import {
  LinkColor,
  LinkHoverColor,
  LinkUnderline,
  LinkWeight,
} from '@freelancer/ui/link';
import { TextSize } from '@freelancer/ui/text';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import { sha256 } from 'js-sha256';

@Component({
  standalone: false,
  selector: 'fl-linkshim',
  template: `
    <fl-link
      [color]="color"
      [newTab]="true"
      [flNativeCopy]
      flTrackingLabel="shimmedLink"
      [hoverColor]="hoverColor"
      [size]="size"
      [link]="processedLink"
      [queryParams]="queryParams"
      [fragment]="fragment"
      [underline]="underline"
      [weight]="weight"
      [isAbsoluteUrl]="true"
      rel="noopener"
    >
      <ng-content></ng-content>
    </fl-link>
  `,
  styleUrls: ['./linkshim.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkshimComponent implements OnChanges {
  private auth = inject(Auth);
  changeDetectorRef = inject(ChangeDetectorRef);
  private router = inject(Router);
  private appsDomainsMap = inject<AppsDomainsMap>(APPS_DOMAINS_MAP);
  private uiConfig = inject<UiConfig>(UI_CONFIG);

  readonly baseUrl = '/users/l.php';

  @Input() color: LinkColor = LinkColor.DEFAULT;
  @Input() hoverColor: LinkHoverColor = LinkHoverColor.DEFAULT;
  @Input() size: TextSize = TextSize.BODY_16;
  @Input() underline: LinkUnderline = LinkUnderline.HOVER;
  @Input() weight: LinkWeight = LinkWeight.INHERIT;
  @Input() link: string;

  queryParams: QueryParams = {};
  processedLink: string;
  fragment: string | undefined;

  private readonly domains = Object.values(
    // TODO: T314332 - Casting is required as `uiConfig.theme` still contains 'deloitte' while deloitte has been removed from Applications.
    // This should be cleaned up after assessing the the likelihood of Deloitte's resurrection
    this.appsDomainsMap[(this.uiConfig.theme as Applications) ?? 'main'],
  ).filter((domain): domain is string => typeof domain === 'string');

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.keys(changes).includes('link')) {
      // If link is already a relative path, treat it as an internal page
      // Relative URLs start with a slash, a dot followed by a slash, or two dots followed by a slash
      if (/^([.]{0,2}\/)/.test(this.link)) {
        this.processedLink = this.link;
        return;
      }

      const destinationURL = new URL(this.link);
      const destinationHostname = destinationURL.hostname;

      // Check if destination URL's hostname matches any of
      // application's valid domains (e.g country domains)
      if (this.domains.includes(destinationHostname)) {
        const destinationUrlTree = this.router.parseUrl(
          this.link.replace(destinationURL.origin, ''), // parse relative URL
        );

        // Treat as internal page by disabling linkshim and
        // using relative path as link
        this.processedLink = destinationURL.pathname;
        this.queryParams = destinationUrlTree.queryParams;
        this.fragment = destinationUrlTree.fragment || undefined;
      } else {
        // Otherwise run URL through linkshim
        this.processedLink = this.baseUrl;
        this.queryParams = {
          url: this.link,
          sig: sha256(this.auth.getCSRFToken() + this.link),
        };
      }
    }
  }
}
